"use client"

import { useCallback, useEffect, useRef } from "react"

import { useAsyncStorage } from "@bullseye/components"

const STORAGE_KEY = "category-filter-discovery"
const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000

type DiscoveryState = {
  status: string
  seededAt?: number
}

function asDiscoveryState(data: unknown): DiscoveryState | null {
  if (typeof data === "object" && data !== null && "status" in data) {
    return data as DiscoveryState
  }
  return null
}

export const useCategoryFilterDiscovery = () => {
  const { data, isLoading, setValue } = useAsyncStorage(STORAGE_KEY)
  const seeded = useRef(false)

  useEffect(() => {
    if (isLoading || seeded.current) return
    if (data === null || data === undefined) {
      seeded.current = true
      void setValue({ status: "new", seededAt: Date.now() })
    }
  }, [isLoading, data, setValue])

  const state = asDiscoveryState(data)

  const isNew =
    !isLoading &&
    state?.status === "new" &&
    typeof state.seededAt === "number" &&
    Date.now() - state.seededAt < FOURTEEN_DAYS_MS

  const markSeen = useCallback(() => {
    const s = asDiscoveryState(data)
    if (s?.status === "new") {
      void setValue({ status: "viewed" })
    }
  }, [data, setValue])

  return { isNew, markSeen }
}
